import Mustache from 'mustache';
import { applyHiddenSections, shouldRenderSection, EMPTY_SECTION_CLEANUP_SCRIPT } from './sectionVisibility.js';

/**
 * Universal CSS normalization block injected into every rendered template.
 * Ensures consistent A4 sizing, page-break behavior, box-sizing,
 * and print output across all 67+ templates without editing each one.
 */
const TEMPLATE_NORMALIZE_CSS = `
<style id="rg-normalize">
  /* ── Box-sizing reset ── */
  *, *::before, *::after { box-sizing: border-box; }

  /* ── A4 body defaults ── */
  body {
    max-width: 210mm;
    margin-left: auto;
    margin-right: auto;
    overflow-wrap: break-word;
    word-wrap: break-word;
  }

  /* ── Ensure text visibility in personal info ── */
  a, [class*="contact"] a, [class*="header"] a {
    word-break: break-all;
  }
  [class*="contact"], [class*="header-contact"], [class*="personal"] {
    overflow-wrap: break-word;
    word-wrap: break-word;
  }

  /* ── Page-break: allow large blocks to split across pages ── */
  /* Only keep small single-line items together */
  .skill-category, .award-item, .language-item {
    page-break-inside: avoid;
    break-inside: avoid;
  }

  /* Experience/education/project blocks: allow splitting so they
     don't jump to the next page leaving blank space */
  .experience-block, .experience-item, .experience-entry,
  .education-block, .education-item, .education-entry,
  .project-block, .project-item, .project-entry,
  .cert-block, .certification-item, .certification-entry,
  .resume-item {
    page-break-inside: auto !important;
    break-inside: auto !important;
  }

  /* Section headings stay with content */
  h2, h3 {
    page-break-after: avoid;
    break-after: avoid;
  }

  /* Sections themselves CAN break across pages (between items) */
  section, .resume-section, .section-container,
  .experience-section, .education-section,
  .projects-section, .certifications-section {
    page-break-inside: auto !important;
    break-inside: auto !important;
  }

  /* ── Page margin rules for PDF export ── */
  /* Page 1: no margins — allow full-bleed headers/ribbons */
  @page :first {
    margin: 0mm;
  }
  /* Page 2+: top margin so content doesn't start at very edge */
  @page {
    margin: 14mm 0mm 8mm 0mm;
  }

  /* ── Print media ── */
  @media print {
    * {
      -webkit-print-color-adjust: exact !important;
      print-color-adjust: exact !important;
      color-adjust: exact !important;
    }
    body {
      background: white !important;
      /* Do NOT strip body padding/margin — templates rely on them for layout.
         Backend uses emulateMediaType('screen') so these rules won't fire
         during PDF generation anyway. Keep only background for any manual print. */
      max-width: none !important;
    }
    /* Remove decorative shadows */
    [class*="resume"], .container, .wrapper {
      box-shadow: none !important;
    }
  }

  /* ── Prevent overflow clipping & blank page ── */
  html, body {
    overflow: visible;
    height: auto !important;
    max-height: none;
  }
</style>
`;

/**
 * Injects normalization CSS into rendered template HTML.
 * Inserted right before </head> if present, else before </style> or start of <body>.
 */
const injectNormalizationCSS = (html) => {
  // Skip if already injected
  if (html.includes('id="rg-normalize"')) return html;
  
  if (html.includes('</head>')) {
    return html.replace('</head>', `${TEMPLATE_NORMALIZE_CSS}</head>`);
  }
  // Fallback: inject before <body>
  if (html.includes('<body')) {
    return html.replace(/<body/i, `${TEMPLATE_NORMALIZE_CSS}<body`);
  }
  // Last fallback: prepend
  return TEMPLATE_NORMALIZE_CSS + html;
};

/**
 * Injects the section cleanup script into rendered template HTML.
 * This script removes empty section headers, orphan HRs, and empty containers.
 * Inserted right before </body> if present.
 */
const injectSectionCleanupScript = (html) => {
  // Skip if already injected
  if (html.includes('id="rg-section-cleanup"')) return html;

  if (html.includes('</body>')) {
    return html.replace('</body>', `${EMPTY_SECTION_CLEANUP_SCRIPT}</body>`);
  }
  // Fallback: append
  return html + EMPTY_SECTION_CLEANUP_SCRIPT;
};

/**
 * Renders a resume template with provided data using Mustache templating
 * @param {string} templateHTML - The HTML template string with Mustache variables
 * @param {object} resumeData - The resume data to populate the template
 * @param {object} options - Options to pass to prepareResumeData
 * @param {string[]} options.hiddenSections - Sections explicitly hidden by user
 * @returns {string} Rendered HTML string
 */
export const renderTemplate = (templateHTML, resumeData, options = {}) => {
  try {
    // Disable HTML escaping to allow HTML content in resume data
    Mustache.escape = (text) => text;
    
    // Apply hidden sections — zero out data for sections the user has hidden
    const { hiddenSections = [], ...restOptions } = options;
    const dataWithHidden = applyHiddenSections(resumeData, hiddenSections);
    
    // Prepare data with additional helper properties
    const preparedData = prepareResumeData(dataWithHidden, restOptions);
    
    // Override boolean flags for hidden sections
    if (hiddenSections.length > 0) {
      if (hiddenSections.includes('experience')) preparedData.hasExperiences = false;
      if (hiddenSections.includes('education')) preparedData.hasEducation = false;
      if (hiddenSections.includes('skills')) {
        preparedData.hasTechnicalSkills = false;
        preparedData.hasSoftSkills = false;
      }
      if (hiddenSections.includes('projects')) preparedData.hasProjects = false;
      if (hiddenSections.includes('certifications')) preparedData.hasCertifications = false;
      if (hiddenSections.includes('languages')) preparedData.hasLanguages = false;
      if (hiddenSections.includes('achievements')) preparedData.hasAchievements = false;
      if (hiddenSections.includes('summary')) preparedData.hasSummary = false;
    }
    
    // Render template with Mustache
    let rendered = Mustache.render(templateHTML, preparedData);
    
    // Replace template <title> with user's name (affects PDF tab/title bar)
    const fullName = preparedData.fullName || 'Resume';
    rendered = rendered.replace(/<title>[^<]*<\/title>/i, `<title>Resume - ${fullName}</title>`);
    
    // Inject universal template normalization CSS
    rendered = injectNormalizationCSS(rendered);
    
    // Inject empty section cleanup script (removes orphan headers/sections)
    rendered = injectSectionCleanupScript(rendered);
    
    return rendered;
  } catch (error) {
    console.error('Error rendering template:', error);
    throw new Error(`Template rendering failed: ${error.message}`);
  }
};

/**
 * Normalize a user-entered URL field into a fully-qualified http(s) URL
 * that is safe to drop into an <a href="..."> attribute.
 *
 * Rules:
 *   ""                       → ""        (Mustache section skips)
 *   "https://x.com"          → "https://x.com"
 *   "http://x.com"           → "http://x.com"
 *   "x.com/me"               → "https://x.com/me"
 *   "@johndoe"               → "https://github.com/johndoe"   (if kind="github")
 *   "linkedin.com/in/jane"   → "https://linkedin.com/in/jane"
 *
 * @param {string} value  Raw user input
 * @param {'linkedin'|'github'|'portfolio'} kind  Used for handle resolution
 * @returns {string}      Cleaned absolute URL or empty string
 */
const normalizeUrl = (value, kind) => {
  if (!value || typeof value !== 'string') return '';
  let v = value.trim();
  if (!v) return '';

  // Handle "@username" shorthand for github/linkedin
  if (v.startsWith('@')) {
    const handle = v.slice(1);
    if (kind === 'github') return `https://github.com/${handle}`;
    if (kind === 'linkedin') return `https://linkedin.com/in/${handle}`;
    return `https://${handle}`;
  }

  // Already absolute
  if (/^https?:\/\//i.test(v)) return v;

  // Strip leading "//" if present (protocol-relative)
  v = v.replace(/^\/\//, '');

  return `https://${v}`;
};

/**
 * Prepares resume data for template rendering
 * Adds computed properties and ensures all arrays exist
 * @param {object} data - Raw resume data
 * @param {object} options - Options for data preparation
 * @param {boolean} options.keepEmptyPlaceholders - If true, keep empty placeholder items for required sections
 * @returns {object} Prepared data for template
 */
export const prepareResumeData = (data, options = {}) => {
  const { keepEmptyPlaceholders = false } = options;
  
  // Templates use flat structure with 'experiences' (plural)
  const experiencesRaw = Array.isArray(data.experiences) ? data.experiences : [];
  const educationRaw = Array.isArray(data.education) ? data.education : [];
  const projectsRaw = Array.isArray(data.projects) ? data.projects : [];
  const certificationsRaw = Array.isArray(data.certifications) ? data.certifications : [];

  // Dedup helpers
  const dedupArray = (arr) => {
    if (!Array.isArray(arr)) return [];
    const seen = new Set();
    const out = [];
    for (const item of arr) {
      const key = typeof item === 'string' ? item.trim() : JSON.stringify(item);
      if (!seen.has(key)) {
        seen.add(key);
        out.push(item);
      }
    }
    return out;
  };

  const dedupBy = (arr, keyFn) => {
    if (!Array.isArray(arr)) return [];
    const seen = new Set();
    const out = [];
    for (const obj of arr) {
      const key = keyFn(obj);
      if (!seen.has(key)) {
        seen.add(key);
        out.push(obj);
      }
    }
    return out;
  };

  // Deduplicate high-level arrays
  let experiences = dedupBy(experiencesRaw, (e) => [e.jobTitle, e.company, e.startDate, e.endDate].filter(Boolean).join('|'));
  let education = dedupBy(educationRaw, (e) => [e.degree, e.institution, e.graduationDate].filter(Boolean).join('|'));
  let projects = dedupBy(projectsRaw, (p) => [p.projectName, p.projectDate, p.projectLink].filter(Boolean).join('|'));
  let certifications = dedupBy(certificationsRaw, (c) => [c.certificationName, c.certificationIssuer, c.certificationDate].filter(Boolean).join('|'));
  
  // Filter out empty entries UNLESS keepEmptyPlaceholders is true
  // BUT: Projects and Certifications are ALWAYS optional - filter them even with keepEmptyPlaceholders
  if (!keepEmptyPlaceholders) {
    experiences = experiences.filter(e => e.jobTitle || e.company || e.startDate);
    education = education.filter(e => e.degree || e.institution);
    projects = projects.filter(p => p.projectName || p.projectDescription || p.name || p.description);
    certifications = certifications.filter(c => c.certificationName || c.certificationIssuer || c.name || c.issuer || c.issuingOrganization);
  } else {
    // Even with keepEmptyPlaceholders, always filter projects and certifications
    // Required sections (experience, education) keep placeholders
    // Optional sections (projects, certifications) get removed if empty
    projects = projects.filter(p => p.projectName || p.projectDescription || p.name || p.description);
    certifications = certifications.filter(c => c.certificationName || c.certificationIssuer || c.name || c.issuer || c.issuingOrganization);
  }
  
  const prepared = {
    // Spread all data first (includes fullName, email, phone, etc.)
    ...data,
    
    // Ensure all sections exist as arrays and format dates
    experiences: experiences.map(exp => {
      // Handle responsibilities: can be string (with newlines) or array
      let responsibilities = [];
      if (typeof exp.responsibilities === 'string') {
        // Split by newlines and clean up
        responsibilities = exp.responsibilities.split('\n').map(r => r.trim()).filter(r => r.length > 0);
      } else if (Array.isArray(exp.responsibilities)) {
        responsibilities = exp.responsibilities;
      } else if (exp.bullets) {
        // Fallback to bullets field
        responsibilities = Array.isArray(exp.bullets) ? exp.bullets : [];
      }

      // Determine if currently working
      const isCurrent = exp.current || exp.currentlyWorking;
      // Set endDate to 'Present' when currently working — templates use {{endDate}} directly
      const resolvedEndDate = isCurrent ? 'Present' : (exp.endDate || '');
      // Format startDate for display
      const formattedStart = formatDate(exp.startDate);
      const formattedEnd = isCurrent ? 'Present' : formatDate(exp.endDate);
      
      return {
        ...exp,
        // Override endDate so templates that use {{endDate}} get 'Present'
        endDate: formattedEnd || resolvedEndDate,
        startDate: formattedStart || exp.startDate || '',
        // Deduplicate bullets/responsibilities within each experience
        responsibilities: dedupArray(responsibilities),
        hasResponsibilities: dedupArray(responsibilities).length > 0,
        dateRange: formatDateRange(exp.startDate, exp.endDate, isCurrent)
      };
    }),
    
    education: education.map(edu => ({
      ...edu,
      // Map educationLocation to both variable names for template compatibility
      educationLocation: edu.educationLocation || edu.location || '',
      location: edu.educationLocation || edu.location || '',
      // Format graduation date for display
      graduationDate: formatDate(edu.graduationDate) || edu.graduationDate || '',
      startDate: formatDate(edu.startDate) || edu.startDate || '',
      endDate: formatDate(edu.endDate) || edu.endDate || '',
      dateRange: formatDateRange(edu.startDate, edu.endDate)
    })),
    
    projects: projects.map(proj => ({
      ...proj,
      // Map V2 form field names to template token names (both directions for compatibility)
      projectName: proj.projectName || proj.name || '',
      name: proj.projectName || proj.name || '',
      projectDescription: proj.projectDescription || proj.description || '',
      description: proj.projectDescription || proj.description || '',
      projectTechnologies: proj.projectTechnologies || proj.technologies || '',
      technologies: proj.projectTechnologies || proj.technologies || '',
      projectDate: proj.projectDate || proj.date || formatDateRange(proj.startDate, proj.endDate) || '',
      date: proj.projectDate || proj.date || formatDateRange(proj.startDate, proj.endDate) || '',
      projectLink: proj.projectLink || proj.link || '',
      link: proj.projectLink || proj.link || '',
      dateRange: proj.date || proj.projectDate || formatDateRange(proj.startDate, proj.endDate)
    })),
    
    certifications: certifications.map(cert => ({
      ...cert,
      // Map V2 form field names to template token names (both directions for compatibility)
      certificationName: cert.certificationName || cert.name || '',
      name: cert.certificationName || cert.name || '',
      certificationIssuer: cert.certificationIssuer || cert.issuingOrganization || cert.issuer || '',
      issuer: cert.certificationIssuer || cert.issuingOrganization || cert.issuer || '',
      issuingOrganization: cert.certificationIssuer || cert.issuingOrganization || cert.issuer || '',
      certificationDate: cert.certificationDate || cert.issueDate || cert.date || '',
      date: cert.certificationDate || cert.issueDate || cert.date || '',
      credentialId: cert.credentialId || '',
      dateDisplay: cert.certificationDate || cert.issueDate || cert.date || cert.year || ''
    })),
    
    // Ensure skills arrays exist, merge tools into technical, and deduplicate
    technicalSkills: dedupArray([
      ...(data.technicalSkills || data.skills || []),
      ...(data.tools || [])
    ]),
    // Pre-joined variants for templates — mustache.js has no {{^@last}} helper,
    // so templates that need a separator use these to avoid trailing separators.
    technicalSkillsJoined: dedupArray([
      ...(data.technicalSkills || data.skills || []),
      ...(data.tools || [])
    ]).join(', '),
    technicalSkillsJoinedSlash: dedupArray([
      ...(data.technicalSkills || data.skills || []),
      ...(data.tools || [])
    ]).join(' / '),
    technicalSkillsJoinedPipe: dedupArray([
      ...(data.technicalSkills || data.skills || []),
      ...(data.tools || [])
    ]).join(' | '),
    technicalSkillsJoinedBullet: dedupArray([
      ...(data.technicalSkills || data.skills || []),
      ...(data.tools || [])
    ]).join(' • '),
    softSkills: dedupArray(data.softSkills || []),
    softSkillsJoined: dedupArray(data.softSkills || []).join(', '),
    softSkillsJoinedBullet: dedupArray(data.softSkills || []).join(' • '),
    softSkillsJoinedPipe: dedupArray(data.softSkills || []).join(' | '),
    tools: dedupArray(data.tools || []),
    
    // Languages array and formatted string
    languages: Array.isArray(data.languages) ? data.languages.map(lang => {
      if (typeof lang === 'string') return { language: lang, proficiency: '', proficiencyPercent: 50 };
      const profLevel = (lang.proficiency || lang.level || '').toLowerCase();
      const percentMap = { native: 100, fluent: 95, advanced: 85, 'upper-intermediate': 75, intermediate: 60, 'pre-intermediate': 45, elementary: 30, beginner: 20 };
      const proficiencyPercent = percentMap[profLevel] || 50;
      return { ...lang, proficiencyPercent };
    }) : [],
    achievements: Array.isArray(data.achievements) ? data.achievements : [],
    languagesFormatted: Array.isArray(data.languages) && data.languages.length > 0
      ? data.languages.map(lang => {
          if (typeof lang === 'string') return lang;
          const name = lang.language || lang.name || '';
          const level = lang.proficiency || lang.level || '';
          return level ? `${name} (${level})` : name;
        }).join(' • ')
      : '',

    // Add helper properties for conditionals
    // Required sections (experience, education) show even if empty when keepEmptyPlaceholders is true
    // Optional sections (projects, certifications) ALWAYS check for actual data
    hasExperiences: keepEmptyPlaceholders ? true : experiences.length > 0,
    hasExperience: keepEmptyPlaceholders ? true : experiences.length > 0,
    hasEducation: keepEmptyPlaceholders ? true : education.length > 0,
    hasTechnicalSkills: dedupArray([...(data.technicalSkills || data.skills || []), ...(data.tools || [])]).length > 0,
    hasSkills: dedupArray([...(data.technicalSkills || data.skills || []), ...(data.tools || []), ...(data.softSkills || [])]).length > 0,
    hasSoftSkills: dedupArray(data.softSkills || []).length > 0,
    
    // Projects and Certifications are OPTIONAL - only show if they have actual data
    hasProjects: projects.length > 0,
    hasCertifications: certifications.length > 0,
    hasLanguages: Array.isArray(data.languages) && data.languages.length > 0,
    hasAchievements: Array.isArray(data.achievements) && data.achievements.length > 0,
    
    hasSummary: (data.summary || '').trim().length > 0,
    
    // Ensure summary exists
    summary: data.summary || '',

    // Computed properties for templates
    firstInitial: (data.fullName || '').charAt(0) || '',

    // linkedIn alias — some templates use {{linkedIn}} (capital I)
    linkedIn: data.linkedin || '',

    // Auto-normalized URLs used by template anchor hrefs. Mustache.escape is
    // disabled so these can safely contain "://" and "?" without re-escaping.
    // Rule: if value already starts with http(s):// keep it. Else prefix
    // https://. If value is empty, output stays empty (and the {{#linkedin}}
    // / {{#github}} / {{#portfolio}} section is skipped by Mustache anyway).
    linkedinUrl: normalizeUrl(data.linkedin, 'linkedin'),
    githubUrl: normalizeUrl(data.github, 'github'),
    portfolioUrl: normalizeUrl(data.portfolio || data.website, 'portfolio'),

    // Photo fields mapping for templates (support multiple token names)
    photoURL: data.photoURL || data.photoDataUri || '',
    photo: data.photoURL || data.photoDataUri || '',
    profilePhoto: data.photoURL || data.photoDataUri || '',
    avatar: data.photoURL || data.photoDataUri || '',
    headshot: data.photoURL || data.photoDataUri || ''
  };
  
  return prepared;
};

/**
 * Formats date range for display
 * @param {string} startDate - Start date
 * @param {string} endDate - End date
 * @param {boolean} current - Whether this is a current position
 * @returns {string} Formatted date range
 */
const formatDateRange = (startDate, endDate, current = false) => {
  if (!startDate) return '';
  
  const start = formatDate(startDate);
  
  if (current) {
    return `${start} - Present`;
  }
  
  if (!endDate) {
    return start;
  }
  
  const end = formatDate(endDate);
  return `${start} - ${end}`;
};

/**
 * Formats a date string for display
 * Handles multiple input formats: YYYY-MM, YYYY-MM-DD, MM/YYYY, "Mon YYYY", "Present"
 * @param {string} dateStr - Date string
 * @returns {string} Formatted date (e.g., "Jan 2024")
 */
const formatDate = (dateStr) => {
  if (!dateStr) return '';
  
  try {
    const trimmed = dateStr.trim();
    
    // Already formatted ("Jan 2024", "Present", etc.) — return as-is
    if (/^(Jan|Feb|Mar|Apr|May|Jun|Jul|Aug|Sep|Oct|Nov|Dec)\s+\d{4}$/i.test(trimmed)) {
      return trimmed;
    }
    if (/^present$/i.test(trimmed)) return 'Present';
    
    const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 
                       'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

    // MM/YYYY format (from resume parser)
    const mmYYYY = trimmed.match(/^(\d{1,2})\/(\d{4})$/);
    if (mmYYYY) {
      const monthIndex = parseInt(mmYYYY[1], 10) - 1;
      if (monthIndex >= 0 && monthIndex < 12) {
        return `${monthNames[monthIndex]} ${mmYYYY[2]}`;
      }
      return mmYYYY[2];
    }

    // YYYY-MM or YYYY-MM-DD format
    const [year, month] = trimmed.split('-');
    
    if (!month) {
      return year; // Just year
    }
    
    const monthIndex = parseInt(month, 10) - 1;
    
    if (monthIndex >= 0 && monthIndex < 12) {
      return `${monthNames[monthIndex]} ${year}`;
    }
    
    return year;
  } catch {
    return dateStr;
  }
};

/**
 * Enhances resume data for preview/download
 * Adds additional formatting and computed properties
 * @param {object} data - Resume data
 * @returns {object} Enhanced data
 */
export const enhanceForPreview = (data) => {
  const enhanced = prepareResumeData(data);
  
  // Add any additional enhancements for preview/download
  // (placeholder for future enhancements)
  
  return enhanced;
};
